import { zodMoment } from '@qapline/helpers';
import { z } from 'zod';

import { carrierSchema } from '../../carrier/schemas/carrier';
import { dispatcherSchema } from '../../carrier/schemas/dispatcher';
import { customerContactSchema } from '../../customer/schemas/customerContact';

export const commissionOriginSchema = z.enum(['UNDEFINED', 'ORDER_NUMBER', 'EMAIL', 'PHONE', 'WEB']);
export type TCommissionOrigin = z.infer<typeof commissionOriginSchema>;

export enum ECommissionState {
    ENQUIRY_FROM_WEBSITE = 'ENQUIRY_FROM_WEBSITE',
    ENQUIRY_FROM_CUSTOMER_ZONE = 'ENQUIRY_FROM_CUSTOMER_ZONE',
    CREATED = 'CREATED',
    ACCEPTED = 'ACCEPTED',
    WAITING_FOR_OFFERS = 'WAITING_FOR_OFFERS',
    WAITING_FOR_PRICE_APPROVAL = 'WAITING_FOR_PRICE_APPROVAL',
    WAITING_FOR_OFFERS_FOR_CUSTOMER = 'WAITING_FOR_OFFERS_FOR_CUSTOMER',
    WAITING_FOR_CUSTOMER_APPROVAL = 'WAITING_FOR_CUSTOMER_APPROVAL',
    CARRIER_ASSIGNED = 'CARRIER_ASSIGNED',
    ORDER_SENT = 'ORDER_SENT',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    IN_CLAIM = 'IN_CLAIM',
    CANCELLED = 'CANCELLED',
    CANCELLATION_APPROVAL = 'CANCELLATION_APPROVAL',
    CANCELLED_WITH_FAILED_ATTEMPT = 'CANCELLED_WITH_FAILED_ATTEMPT',
}
export const commissionStateSchema = z.nativeEnum(ECommissionState);
export type TCommissionState = z.infer<typeof commissionStateSchema>;

export enum ECustomerCommissionState {
    ENQUIRY = 'ENQUIRY',
    PROCESSING = 'PROCESSING',
    WAITING_FOR_OFFERS = 'WAITING_FOR_OFFERS',
    WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
    VEHICLE_ASSIGNED = 'VEHICLE_ASSIGNED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    IN_CLAIM = 'IN_CLAIM',
    CANCELLED = 'CANCELLED',
    CANCELLATION_APPROVAL = 'CANCELLATION_APPROVAL',
    CANCELLED_WITH_FAILED_ATTEMPT = 'CANCELLED_WITH_FAILED_ATTEMPT',
}
export const customerCommissionStateSchema = z.nativeEnum(ECustomerCommissionState);
export type TCustomerCommissionState = z.infer<typeof customerCommissionStateSchema>;

export const commissionSchema = z.object({
    commission_id: z.number(),
    tsAdded: zodMoment(),
    addedBy_id: z.number(),

    tsEdited: zodMoment().nullable(),
    tsDeleted: zodMoment().nullable(),
    deleted: z.boolean(),

    origin: commissionOriginSchema,
    originValue: z.string().nullable(),
    state: commissionStateSchema,
    relation: z.string().nullable(),
    number: z.number().nullable(),
    qid: z.string().nullable(),
    price: z.number({ coerce: true }).nullable(),
    vat: z.number().nullable(),
    currency: z.string().nullable(),
    currencyExchangeRate: z.number({ coerce: true }).nullable(),
    orderDate: zodMoment().nullable(),
    noteInternal: z.string().nullable(),
    noteDisposition: z.string().nullable(),
    noteCustomer: z.string().nullable(),
    noteCarrier: z.string().nullable(),
    notificationInternal: z.boolean(),
    notificationCustomer: z.boolean(),

    invoice_id: z.number().nullable(),

    // Snapshots taken before contactEmail existed do not carry it, and they are
    // never re-written, so it stays optional here even though a live Dispatcher
    // always has one.
    dispatcherSnapshot: dispatcherSchema.extend({ contactEmail: z.string().email().optional() }).nullable(),
    carrierSnapshot: carrierSchema.nullable(),
    carrierDriver: z.string().nullable(),
    carrierGsm: z.string().nullable(),
    carrierRegistrationPlate: z.string().nullable(),
    carrierPrice: z.number({ coerce: true }).nullable(),
    carrierCurrency: z.string().nullable(),
    carrierCurrencyExchangeRate: z.number({ coerce: true }).nullable(),
    carrierAssignedBy_id: z.number().nullable(),

    customerVersion_id: z.number().nullable(),
    customerContactSnapshot: customerContactSchema.nullable(),
    customerPrice: z.number({ coerce: true }).nullable(),
    customerCurrency: z.string().nullable(),
    customerCurrencyExchangeRate: z.number({ coerce: true }).nullable(),

    customerPriceCZK: z.number({ coerce: true }).nullable(),
    carrierPriceCZK: z.number({ coerce: true }).nullable(),
    provisionCZK: z.number({ coerce: true }).nullable(),

    firstLoading_id: z.number().nullable(),
    lastDischarge_id: z.number().nullable(),
    routeDistanceKm: z.number().int().nonnegative().nullable(),

    minLoadingMeters: z.number({ coerce: true }).nullable(),
    minLoadingWeight: z.number({ coerce: true }).nullable(),

    earlyPayOffered: z.boolean().optional(),
    earlyPayAccepted: z.boolean().nullable().optional(),
    earlyPayDiscount: z.number().nullable().optional(),
});
export type TCommission = z.infer<typeof commissionSchema>;
